import { Alert } from 'react-bootstrap'
import * as H from 'history'
import  React from 'react'
import Button from 'react-bootstrap/Button'
import { ClaimController } from '../../controllers/ClaimController'
import { ClaimModel } from '../../models/ClaimModel'
import { Consultant } from '../../models/Consultant'
import { ISupportSpinner } from '../../shared/Ajax'
import { Backdrop } from '../../shared/Backdrop'
import {
  Checkbox, FormItem, MultiLineField, NumberField, validateFields,
} from '../../shared/fields'
import { HelpForText } from '../../shared/fields/HelpForText'
import { Option } from '../../shared/fields/Options'
import { NextBackButtons } from './controls/NextBackButtons'
import { documentTypeInfo } from './documents/DocumentTypeInfo'
import { calculateEarnings, calculateExpenses, GrantEstimation } from './GrantEstimation'
import { ApplicationForm1PreviewPage } from './PreviewPage'
import { Table } from '../../shared/table'
import { MarketShare } from '../../models/MarketShare'
import { marketShareSummaryRows, MarketShareDetails, marketShareColumns } from './otherTables/marketShare/MarketShareDetails'

export interface ReviewPageProps {
    history: H.History
    model: ClaimModel
    onChange: { (changes: Partial<ClaimModel>): any }
    onSubmit: { (claimAutoNumber: string, callback?: { (): any }): any }
    preview?: boolean
    consultants: Consultant[]
    markets: Option<string>[]
    addressCountries: Option<string>[]
    lowRiskMarkets: string[]
}

export interface ReviewPageState {
    loading: boolean
    pdfContent: boolean
    submissionError: string
}

export class ReviewPage extends React.Component<ReviewPageProps, ReviewPageState> implements ISupportSpinner {
    marketShare: Table<MarketShare>
    acknowledgeIneligibleExpenditureExcluded: Checkbox
    acknowledgeInfoUse: Checkbox
    acknowledgeFalseInfo: Checkbox
    notes: MultiLineField
    formHours: NumberField
    previewContent: HTMLDivElement
    submittedErrorDiv: HTMLDivElement

    ajax: ClaimController

    constructor(props: ReviewPageProps) {
      super(props)
      this.ajax = new ClaimController()
      this.state = {
        loading: false,
        pdfContent: false,
        submissionError: '',
      }
    }

    componentWillUnmount() {
      this.ajax.cancel()
    }

    toggleSpinner(on: boolean, callback: { (): any }): void {
      this.setState({ loading: on }, callback)
    }

    submit = () => {
      validateFields((res) => {
        if (res) {
          if (confirm('You are about to submit the application. Proceed?')) {
            this.setState({ pdfContent: true }, () => {
              const content = this.previewContent.innerHTML
              this.setState({ pdfContent: false }, () => {
                const requiredDocuments: string[] = []
                for (const t of documentTypeInfo) {
                  const items = t.reducer(this.props.model, this.props.lowRiskMarkets)
                  for (const i of items) {
                    requiredDocuments.push(`${t.code}|${i}`)
                  }
                }

                this.ajax.submit(this, { model: this.props.model, content, requiredDocuments },
                  (res) => {
                    if (res.errorMessage) {
                      this.setState({ submissionError: res.errorMessage })
                    } else {
                      this.props.onSubmit(res.autoClaimNumber, () => {
                        this.props.history.push(`/claim/${this.props.model.claimId}/submission`)
                      })
                    }
                  })
              })
            })
          }
        }
      },
      this.marketShare,
      this.acknowledgeIneligibleExpenditureExcluded,
      this.acknowledgeInfoUse,
      this.acknowledgeFalseInfo,
      this.notes,
      this.formHours)
    }

    render() {
      const expenseTotal = calculateExpenses(this.props.model).total
      const earningsTotal = calculateEarnings(this.props.model).total

      return (
        <div>
          <GrantEstimation model={this.props.model} showSummary />

          <h3>Summary of Expenses and Earnings</h3>
          <p>Click Add/Edit to input an estimate of the expenses and exports for top 6 countries</p>

          <Table
                <MarketShare>
            ref={(r) => this.marketShare = r}
            header={<Button id="btnAddMarketShare" className="emdg-btn prepare-btn" onClick={(e) => this.props.history.push(`/claim/${this.props.model.claimId}/prepare/marketshare`)}>Add/Edit</Button>}
            label="Total of Expenses and Earnings"
            id="marketShare"
            value={this.props.model.marketShare}
            summaryRows={marketShareSummaryRows(this.props.model.marketShare, expenseTotal, earningsTotal)}
            onChange={(marketShare) => this.props.onChange({ marketShare })}
            columns={marketShareColumns(this.props.markets, expenseTotal, earningsTotal, this.props.model.claimYear)}
            renderSection={(p) => <MarketShareDetails {...p} marketsAlreadyAllocated={this.props.model.marketShare} markets={this.props.markets} claimYear={this.props.model.claimYear} expenseTotal={expenseTotal} earningTotal={earningsTotal} preview />}
            preview={this.props.preview}
            required
            alwaysShowAllRows
          />

          <h3>Acknowledgement</h3>
          <FormItem label={(
            <span className="form-content-preview" style={{ fontSize: this.props.preview ? '10pt' : 'inherits' }}>
              {this.props.preview ? '' : 46.0}
              {' '}
              The following ineligible expenditure must be excluded from the EMDG claim:
              <ul>
                <li>
                  Expenses arising from product development, production, certification or distribution, from after sales service or from seeking investment.
                  <HelpForText
                    id="productDevelopmentExpenses-help"
                    message={(
                      <>
    <h5>Promotion of your products.</h5>
    {' '}
    <p>
              Only expenses relating to the<a href="https://www.austrade.gov.au/ArticleDocuments/6917/form-help.pdf.aspx" target="_blank" rel="noreferrer">promotion</a>
              {' '}
              of your product are eligible.
</p>
  </>
)}
                  />
                </li>
                <li>
                  <span>
                    Expenses that were
                    <a href="https://www.austrade.gov.au/ArticleDocuments/6917/form-help.pdf.aspx" target="_blank" rel="noreferrer">incurred</a>
                    {' '}
                    by a related business entity.
                  </span>
                  {' '}
                  <HelpForText
                    id="relatedBusinessExpenses-help"
                    message={(
                      <>
                        <h5>Paid for by the Applicant</h5>
                        {' '}
                        <p>You can only claim for expenses that have been "paid for" by the Applicant.</p>
                      </>
)}
                  />
                </li>
                <li>
                  <span>
                    Expenses incurred by you when you were not a
                    <a href="https://www.legislation.gov.au/Details/C2016C00793/Html/Text#_Toc456170224" target="_blank" rel="noreferrer">resident</a>
                    {' '}
                    of Australia.
                  </span>
                  {' '}
                  <HelpForText
                    id="residentExpenses-help"
                    message={(
                      <>
                        <h5>Resident of Australia</h5>
                        {' '}
                        <p>
      You can only claim expenses during the period that you were a<a href="https://www.legislation.gov.au/Details/C2012C00076/Html/Text#_Toc313553365" target="_blank" rel="noreferrer">resident</a>
      {' '}
      of Australia and carrying on business in Australia.
</p>
                      </>
)}
                  />
                </li>
                <li>
                  <span>
                    Expenses that are a
                    <a href=" https://www.legislation.gov.au/Details/C2016C00793/Html/Text#_Toc456170125" target="_blank">commission</a>
                    {' '}
                    or discount.
                  </span>
                  {' '}
                  <HelpForText
                    id="commissionExpenses-help"
                    message={(
                      <>
                        <h5>Commission and discounts</h5>
                        {' '}
                        <p>
      Sales related expenses such as<a href="https://www.legislation.gov.au/Details/C2012C00076/Html/Text#_Toc313553273" target="_blank" rel="noreferrer">commission</a>
      {' '}
      and discounts are not eligible.
</p>
                      </>
)}
                  />
                </li>
                <li>
                  <span>
                    Expenses incurred in payment of an Australian
                    <a href="https://www.legislation.gov.au/Details/C2012C00076/Html/Text#_Toc313553272" target="_blank" rel="noreferrer">tax</a>
                    , levy or charge (except Australian departure tax).
                  </span>
                  {' '}
                  <HelpForText
                    id="taxExpenses-help"
                    message={(
                      <>
                        <h5>Australian tax, levy or charge</h5>
                        {' '}
                        <p>Claims for expenses incurred must exclude GST and other Australian taxes.</p>
                      </>
)}
                  />
                </li>
                <li>
                  <span>
                    Expenses that relate to an illegal or
                    <a href="https://www.legislation.gov.au/Details/C2012C00076/Html/Text#_Toc313553276" target="_blank" rel="noreferrer">unlawful</a>
                    {' '}
                    activity.
                  </span>
                </li>
                <li>
                  <span>
                    Expenses that relate to any form of pornographic material with a classification equivalent to
                    <a href=" https://www.legislation.gov.au/Details/C2016C00793/Html/Text#_Toc456170130" target="_blank">X-rating</a>
                    {' '}
                    for films.
                  </span>
                </li>
                <li>
                  <span>
                    <a href="https://www.austrade.gov.au/ArticleDocuments/6917/form-help.pdf.aspx" target="_blank" rel="noreferrer">Cash</a>
                    {' '}
                    payments exceeding A$10,000 per application.
                  </span>
                </li>
                <li>
                  <span>
                    Expenses that were
                    <a href="https://www.austrade.gov.au/ArticleDocuments/6917/form-help.pdf.aspx" target="_blank" rel="noreferrer">not paid</a>
                    {' '}
                    by you in the grant year (date of payment is taken to be the date that your bank, financial institution or credit card is debited).
                  </span>
                </li>
                <li>Expenses relating to and Export Earnings from the Democratic People's Republic of Korea (North Korea) or New Zealand.</li>
              </ul>
            </span>
)}
          >
            <Checkbox
              ref={(r) => this.acknowledgeIneligibleExpenditureExcluded = r}
              id="acknowledgeIneligibleExpenditureExcluded"
              label="I have NOT included any of these expenses in any schedules in the EMDG claim"
              onChange={(acknowledgeIneligibleExpenditureExcluded) => this.props.onChange({ acknowledgeIneligibleExpenditureExcluded })}
              value={this.props.model.acknowledgeIneligibleExpenditureExcluded}
              required
              preview={this.props.preview}
            />
          </FormItem>

          {this.props.preview && <hr className="form-content-preview" />}

          <FormItem
            label={(
              <span className="form-content-preview" style={{ fontSize: this.props.preview ? '10pt' : 'inherits' }}>
                {this.props.preview ? '' : 47.0}
                {' '}
                I acknowledge that information listed below from application may be used in other parts of Austrade to support its trade promotion activities. The use of this information will be protected with safeguards in line with the Australian Privacy Principles (APPs) in the Privacy Act 1988 (Privacy Act).
                <br />
                List of information to be shared:
                <ul>
                  <li>your contact details (including your name, position, contact phone number and email)</li>
                  <li>details of the product or service that you are promoting overseas</li>
                  <li>the value of the marketing expenses you are claiming and the countries they relate to</li>
                  <li>the value of your export earnings and the countries they relate to</li>
                  <li>your income (of the applicant)</li>
                  <li>the number of people employed by you, the applicant</li>
                </ul>
              </span>
)}
          >
            <Checkbox
              ref={(r) => this.acknowledgeInfoUse = r}
              id="acknowledgeInfoUse"
              label="I have read the EMDG Confidentiality and Privacy notice and provide my consent"
              onChange={(acknowledgeInfoUse) => this.props.onChange({ acknowledgeInfoUse })}
              value={this.props.model.acknowledgeInfoUse}
              required
              preview={this.props.preview}
            />
          </FormItem>

          {this.props.preview && <hr className="form-content-preview" />}

          <FormItem
            label={(
              <span className="form-content-preview" style={{ fontSize: this.props.preview ? '10pt' : 'inherits' }}>
                {this.props.preview ? '' : 48.0}
                {' '}
                I understand that 'Giving false or misleading information is a serious offence. It is a criminal offence to attempt to obtain or increase the amount of a grant through deceit, false or misleading representation or other unlawful means
              </span>
)}
          >
            <Checkbox
              ref={(r) => this.acknowledgeFalseInfo = r}
              id="acknowledgeFalseInfo"
              label="I understand"
              onChange={(acknowledgeFalseInfo) => this.props.onChange({ acknowledgeFalseInfo })}
              value={this.props.model.acknowledgeFalseInfo}
              required
              preview={this.props.preview}
            />
          </FormItem>

          {this.props.preview && <hr className="form-content-preview" />}

          <MultiLineField
            ref={(r) => this.notes = r}
            id="notes"
            label="Please enter any additional notes in the space provided below"
            value={this.props.model.notes}
            onChange={(notes) => this.props.onChange({ notes })}
            maxLength={3600}
            rowNumber={6}
            preview={this.props.preview}
          />

          {this.props.preview && <hr className="form-content-preview" />}

          <NumberField
            ref={(r) => this.formHours = r}
            id="formHours"
            label="Please estimate the number of hours it has taken you to complete this form"
            value={this.props.model.formHours}
            block={false}
            onChange={(formHours) => this.props.onChange({ formHours })}
            max={9999}
            required
            preview={this.props.preview}
          />

          <div tabIndex={-1} ref={(r) => this.submittedErrorDiv = r} className="mt-3" style={{ display: this.state.submissionError ? '' : 'none' }}>
            <Alert className="emdg-page-alerts emdg-page-alerts--error">
              {this.state.submissionError}
            </Alert>
          </div>

          <NextBackButtons
            onBack={() => this.props.history.push(`/claim/${this.props.model.claimId}/prepare`)}
            nextLabel="Submit"
            onNext={this.submit}
            preview={this.props.preview}
          />

          {this.state.pdfContent
            ? (
              <>
                <div ref={(r) => this.previewContent = r}>
                  <ApplicationForm1PreviewPage model={this.props.model} onChange={this.props.onChange} onSubmit={this.props.onSubmit} history={this.props.history} markets={this.props.markets} consultants={this.props.consultants} addressCountries={this.props.addressCountries} lowRiskMarkets={this.props.lowRiskMarkets} />
                </div>
              </>
            )
            : <></>}

          <Backdrop on={this.state.loading} />
        </div>
      )
    }
}
